import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface ApprovalResult {
  success: boolean;
  message?: string;
  username?: string;
  name?: string;
  role?: string;
  error?: string;
}

export default function ApprovalResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<ApprovalResult | null>(null);
  const [loading, setLoading] = useState(true);
  const hasCalledApi = useRef(false);

  useEffect(() => {
    // Prevent duplicate API calls
    if (hasCalledApi.current) {
      return;
    }
    hasCalledApi.current = true;

    const token = searchParams.get('token');
    const role = searchParams.get('role');

    if (!token || !role) {
      setResult({
        success: false,
        error: '잘못된 승인 링크입니다.'
      });
      setLoading(false);
      return;
    }

    // Call approval API
    fetch(`${process.env.REACT_APP_API_URL || ''}/auth/approve?token=${token}&role=${role}`)
      .then(async (response) => {
        const data = await response.json();
        setResult(data);
        setLoading(false);
      })
      .catch((error) => {
        setResult({
          success: false,
          error: '승인 처리 중 오류가 발생했습니다.'
        });
        setLoading(false);
      });
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <>
        <style>{`
          .approval-loading {
            width: 100vw;
            height: 100vh;
            overflow: hidden;
            position: fixed;
            top: 0;
            left: 0;
            z-index: 9999;
            background-image: url(/fast_bg.gif);
            background-size: 140%;
            background-position: center;
            background-repeat: no-repeat;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }

          .bg-overlay-loading {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0) 100%);
            pointer-events: none;
            z-index: 1;
          }

          .spinner {
            position: relative;
            z-index: 2;
            width: 64px;
            height: 64px;
            border: 4px solid rgba(255, 255, 255, 0.2);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .loading-text {
            position: relative;
            z-index: 2;
            margin-top: 2rem;
            color: white;
            font-size: 1.125rem;
            font-weight: 500;
            font-family: 'Rajdhani', sans-serif;
            letter-spacing: 0.1em;
          }
        `}</style>
        <div className="approval-loading">
          <div className="bg-overlay-loading"></div>
          <div className="spinner"></div>
          <div className="loading-text">승인 처리 중...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');

        .approval-container {
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
          z-index: 9999;
          background-image: url(/fast_bg.gif);
          background-size: 140%;
          background-position: center;
          background-repeat: no-repeat;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .bg-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0) 100%);
          pointer-events: none;
          z-index: 1;
        }

        .approval-card {
          position: relative;
          z-index: 2;
          background: rgba(17, 24, 39, 0.92);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          padding: 3rem 2.5rem;
          width: 100%;
          max-width: 500px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          text-align: center;
          animation: cardFadeIn 0.4s ease-out;
        }

        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .icon-container {
          width: 120px;
          height: 120px;
          margin: 0 auto 2rem;
          position: relative;
        }

        .icon-circle {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .icon-circle.success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 10px 40px rgba(16, 185, 129, 0.4);
        }

        .icon-circle.info {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          box-shadow: 0 10px 40px rgba(59, 130, 246, 0.4);
        }

        .icon-circle.error {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          box-shadow: 0 10px 40px rgba(239, 68, 68, 0.4);
        }

        @keyframes scaleIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .checkmark {
          width: 60px;
          height: 60px;
          stroke: white;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-linejoin: round;
          fill: none;
          animation: drawCheck 0.6s ease-out 0.3s forwards;
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
        }

        @keyframes drawCheck {
          to {
            stroke-dashoffset: 0;
          }
        }

        .info-icon {
          width: 60px;
          height: 60px;
          stroke: white;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-linejoin: round;
          fill: none;
          animation: fadeIn 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }

        .error-cross {
          width: 60px;
          height: 60px;
          stroke: white;
          stroke-width: 3;
          stroke-linecap: round;
          animation: drawCross 0.6s ease-out 0.3s forwards;
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
        }

        @keyframes drawCross {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        .approval-title {
          font-family: 'Orbitron', monospace;
          font-size: 2rem;
          font-weight: 800;
          margin-bottom: 1rem;
          letter-spacing: 0.05em;
        }

        .approval-title.success {
          color: #10b981;
          text-shadow: 0 2px 8px rgba(16, 185, 129, 0.5);
        }

        .approval-title.error {
          color: #ef4444;
          text-shadow: 0 2px 8px rgba(239, 68, 68, 0.5);
        }

        .approval-message {
          font-family: 'Rajdhani', sans-serif;
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
          margin-bottom: 2rem;
          letter-spacing: 0.05em;
        }

        .info-box {
          background: rgba(31, 41, 55, 0.5);
          border: 2px solid rgba(16, 185, 129, 0.3);
          border-radius: 20px;
          padding: 1.5rem;
          margin: 2rem 0;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 0.75rem 0;
          padding: 0.75rem 0;
        }

        .info-row:not(:last-child) {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .info-label {
          font-family: 'Rajdhani', sans-serif;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .info-value {
          color: rgba(255, 255, 255, 0.95);
          font-weight: 600;
          font-size: 1rem;
        }

        .role-badge {
          display: inline-block;
          padding: 0.5rem 1.25rem;
          border-radius: 24px;
          font-weight: 700;
          font-size: 0.875rem;
          color: white;
          letter-spacing: 0.05em;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .role-badge.admin {
          background: linear-gradient(135deg, #B73F3F 0%, #8a2a2a 100%);
        }

        .role-badge.viewer {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }

        .error-box {
          background: rgba(31, 41, 55, 0.5);
          border: 2px solid rgba(239, 68, 68, 0.3);
          border-radius: 20px;
          padding: 1.5rem;
          margin: 2rem 0;
          box-shadow: 0 4px 16px rgba(239, 68, 68, 0.2);
        }

        .error-text {
          color: rgba(252, 165, 165, 0.95);
          font-weight: 600;
          font-size: 1rem;
        }

        .login-button {
          margin-top: 2rem;
          padding: 1.125rem 2.5rem;
          background: linear-gradient(135deg, #B73F3F 0%, #8a2a2a 100%);
          border: none;
          border-radius: 24px;
          color: white;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Rajdhani', sans-serif;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          box-shadow: 0 8px 16px rgba(183, 63, 63, 0.3);
        }

        .login-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px rgba(183, 63, 63, 0.4);
        }

        .login-button:active {
          transform: translateY(0);
        }

        .footer-note {
          margin-top: 2rem;
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.875rem;
          font-family: 'Rajdhani', sans-serif;
          font-weight: 500;
        }
      `}</style>

      <div className="approval-container">
        <div className="bg-overlay"></div>
        <div className="approval-card">
          {result?.success ? (
            <>
              <div className="icon-container">
                <div className="icon-circle success">
                  <svg className="checkmark" viewBox="0 0 52 52">
                    <path d="M14 27l8 8 16-16" />
                  </svg>
                </div>
              </div>
              <h1 className="approval-title success">계정 승인 완료</h1>
              <p className="approval-message">
                사용자 계정이 성공적으로 활성화되었습니다.
              </p>

              <div className="info-box">
                <div className="info-row">
                  <span className="info-label">이름</span>
                  <span className="info-value">{result.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">아이디</span>
                  <span className="info-value">{result.username}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">승인된 역할</span>
                  <span className={`role-badge ${result.role?.toLowerCase()}`}>
                    {result.role}
                  </span>
                </div>
              </div>

              <p className="footer-note">
                이제 로그인하여 시스템을 사용할 수 있습니다.
              </p>

              <button className="login-button" onClick={handleGoToLogin}>
                로그인 화면으로
              </button>
            </>
          ) : (
            <>
              {result?.error === 'User is already approved' ? (
                <>
                  <div className="icon-container">
                    <div className="icon-circle info">
                      <svg className="info-icon" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <circle cx="12" cy="8" r="0.5" fill="white" />
                      </svg>
                    </div>
                  </div>
                  <h1 className="approval-title" style={{ color: '#3b82f6', textShadow: '0 2px 8px rgba(59, 130, 246, 0.5)' }}>
                    이미 승인된 계정
                  </h1>
                  <p className="approval-message">
                    이 계정은 이미 승인되어 활성화되었습니다.
                  </p>

                  <div style={{
                    background: 'rgba(31, 41, 55, 0.5)',
                    border: '2px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '20px',
                    padding: '1.5rem',
                    margin: '2rem 0',
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)'
                  }}>
                    <div style={{ color: 'rgba(147, 197, 253, 0.95)', fontWeight: 600, fontSize: '1rem' }}>
                      계정이 이미 활성화되어 있습니다. 바로 로그인하실 수 있습니다.
                    </div>
                  </div>

                  <p className="footer-note">
                    로그인 화면에서 계정 정보를 입력하세요.
                  </p>

                  <button className="login-button" onClick={handleGoToLogin}>
                    로그인 화면으로
                  </button>
                </>
              ) : (
                <>
                  <div className="icon-container">
                    <div className="icon-circle error">
                      <svg className="error-cross" viewBox="0 0 52 52">
                        <line x1="16" y1="16" x2="36" y2="36" />
                        <line x1="36" y1="16" x2="16" y2="36" />
                      </svg>
                    </div>
                  </div>
                  <h1 className="approval-title error">승인 실패</h1>
                  <p className="approval-message">
                    사용자 계정 승인 중 문제가 발생했습니다.
                  </p>

                  <div className="error-box">
                    <div className="error-text">
                      {result?.error || '알 수 없는 오류가 발생했습니다.'}
                    </div>
                  </div>

                  <p className="footer-note">
                    문제가 지속되면 관리자에게 문의하세요.
                  </p>

                  <button className="login-button" onClick={handleGoToLogin}>
                    메인 화면으로
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
